import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Calendar,
  DollarSign,
  User,
  Building2,
  Image as ImageIcon,
  TrendingUp,
  CheckCircle2,
  Clock,
  PauseCircle,
  XCircle,
  AlertCircle,
  Plus,
  X,
  Upload,
  Camera
} from 'lucide-react';
import { useLandlordStore, ProjectStatus } from '@/stores/useLandlordStore';
import { LoadingScreen } from '@/components/common/LoadingSpinner';
import { useToast } from '@/contexts/ToastContext';

const statusColors = {
  'Planned': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  'In Progress': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  'Completed': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  'On Hold': 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
  'Cancelled': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
};

const statusIcons = {
  'Planned': Clock,
  'In Progress': TrendingUp,
  'Completed': CheckCircle2,
  'On Hold': PauseCircle,
  'Cancelled': XCircle
};

export const ProjectDetails: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { projects, fetchProjects, deleteProject, updateProjectStatus, updateProjectProgress } = useLandlordStore();
  const { success, error: toastError, warning } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [newProgress, setNewProgress] = useState(0);

  useEffect(() => {
    const loadProject = async () => {
      if (projects.length === 0) {
        await fetchProjects();
      }
      setIsLoading(false);
    };
    loadProject();
  }, [projectId, projects.length, fetchProjects]);

  const project = projects.find(p => p.id === projectId);

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!project) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-light-card dark:bg-dark-card p-12 rounded-lg border border-dashed border-light-border dark:border-dark-border text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-light-subtle dark:text-dark-subtle mb-4" />
          <h3 className="text-lg font-medium text-light-text dark:text-dark-text">Project not found</h3>
          <p className="text-light-subtle dark:text-dark-subtle mt-2">The project you are looking for does not exist.</p>
          <button
            onClick={() => navigate('/landlord/projects')}
            className="mt-4 bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors"
          >
            Back to Projects
          </button>
        </div>
      </div>
    );
  }

  const StatusIcon = statusIcons[project.status];
  const budgetPercentage = project.budget_estimated > 0 
    ? (project.budget_actual / project.budget_estimated) * 100 
    : 0;
  const isOverBudget = budgetPercentage > 100;

  const handleDelete = async () => {
    if (!projectId) return;
    
    setIsDeleting(true);
    try {
      await deleteProject(projectId);
      success('Project deleted', 'The project has been removed successfully.');
      navigate('/landlord/projects');
    } catch (error) {
      console.error('Failed to delete project:', error);
      toastError('Delete failed', 'Failed to delete project. Please try again.');
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleStatusChange = async (newStatus: ProjectStatus) => {
    if (!projectId) return;
    
    try {
      await updateProjectStatus(projectId, newStatus);
      success('Status updated', `Project status changed to ${newStatus}.`);
      setShowStatusModal(false);
    } catch (error) {
      console.error('Failed to update status:', error);
      toastError('Update failed', 'Failed to update project status.');
    }
  };

  const handleProgressUpdate = async () => {
    if (!projectId) return;
    
    try {
      await updateProjectProgress(projectId, newProgress);
      success('Progress updated', `Project progress updated to ${newProgress}%.`);
      setShowProgressModal(false);
    } catch (error) {
      console.error('Failed to update progress:', error);
      toastError('Update failed', 'Failed to update project progress.');
    }
  };

  return (
    <div className="max-w-6xl mx-auto pb-12 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between bg-light-card dark:bg-dark-card p-4 rounded-lg shadow-sm border border-light-border dark:border-dark-border">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
          >
            <ArrowLeft size={24} className="text-primary" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-light-text dark:text-dark-text">
              {project.name}
            </h1>
            <p className="text-sm text-light-subtle dark:text-dark-subtle">
              {project.type} • {project.property_name}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/landlord/projects/edit/${projectId}`)}
            className="flex items-center gap-2 px-4 py-2 border border-light-border dark:border-dark-border rounded-lg hover:bg-light-bg dark:hover:bg-dark-bg transition-colors text-light-text dark:text-dark-text"
          >
            <Edit size={18} />
            Edit
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="flex items-center gap-2 px-4 py-2 border border-red-500 text-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
          >
            <Trash2 size={18} />
            Delete
          </button>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Status & Priority */}
          <div className="bg-light-card dark:bg-dark-card p-6 rounded-xl shadow-sm border border-light-border dark:border-dark-border">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2 ${statusColors[project.status]}`}>
                  <StatusIcon size={16} />
                  {project.status}
                </span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  project.priority === 'Urgent' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                  project.priority === 'High' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' :
                  project.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                  'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                }`}>
                  {project.priority} Priority
                </span>
              </div>
              <button
                onClick={() => setShowStatusModal(true)}
                className="text-sm text-primary hover:underline"
              >
                Change Status
              </button>
            </div>
            <p className="text-light-text dark:text-dark-text">{project.description}</p>
          </div>

          {/* Progress */}
          <div className="bg-light-card dark:bg-dark-card p-6 rounded-xl shadow-sm border border-light-border dark:border-dark-border">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-light-text dark:text-dark-text">Progress</h3>
              <button
                onClick={() => setShowProgressModal(true)}
                className="text-sm text-primary hover:underline"
              >
                Update Progress
              </button>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-light-subtle dark:text-dark-subtle">Completion</span>
                <span className="font-bold text-light-text dark:text-dark-text">{project.progress_percentage}%</span>
              </div>
              <div className="w-full bg-light-bg dark:bg-dark-bg rounded-full h-3">
                <div
                  className="bg-primary h-3 rounded-full transition-all duration-300"
                  style={{ width: `${project.progress_percentage}%` }}
                />
              </div>
            </div>
          </div>

          {/* Budget Tracking */}
          <div className="bg-light-card dark:bg-dark-card p-6 rounded-xl shadow-sm border border-light-border dark:border-dark-border">
            <h3 className="font-bold text-light-text dark:text-dark-text mb-4">Budget Tracking</h3>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="p-4 bg-light-bg dark:bg-dark-bg rounded-lg">
                <p className="text-sm text-light-subtle dark:text-dark-subtle">Estimated</p>
                <p className="text-xl font-bold text-light-text dark:text-dark-text">
                  KES {project.budget_estimated.toLocaleString()}
                </p>
              </div>
              <div className="p-4 bg-light-bg dark:bg-dark-bg rounded-lg">
                <p className="text-sm text-light-subtle dark:text-dark-subtle">Actual Spend</p>
                <p className={`text-xl font-bold ${isOverBudget ? 'text-red-500' : 'text-light-text dark:text-dark-text'}`}>
                  KES {project.budget_actual.toLocaleString()}
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-light-subtle dark:text-dark-subtle">Budget Utilization</span>
                <span className={`font-medium ${isOverBudget ? 'text-red-500' : 'text-light-text dark:text-dark-text'}`}>
                  {Math.round(budgetPercentage)}%
                </span>
              </div>
              <div className="w-full bg-light-bg dark:bg-dark-bg rounded-full h-3">
                <div
                  className={`h-3 rounded-full transition-all duration-300 ${isOverBudget ? 'bg-red-500' : 'bg-green-500'}`}
                  style={{ width: `${Math.min(budgetPercentage, 100)}%` }}
                />
              </div>
              {isOverBudget && (
                <div className="flex items-center gap-2 text-red-500 text-sm">
                  <AlertCircle size={16} />
                  <span>Over budget by {Math.round(budgetPercentage - 100)}%</span>
                </div>
              )}
            </div>
          </div>

          {/* Description & Notes */}
          {project.notes && (
            <div className="bg-light-card dark:bg-dark-card p-6 rounded-xl shadow-sm border border-light-border dark:border-dark-border">
              <h3 className="font-bold text-light-text dark:text-dark-text mb-4">Additional Notes</h3>
              <p className="text-light-text dark:text-dark-text whitespace-pre-wrap">{project.notes}</p>
            </div>
          )}
        </div>

        {/* Right Column - Sidebar */}
        <div className="space-y-6">
          {/* Timeline */}
          <div className="bg-light-card dark:bg-dark-card p-6 rounded-xl shadow-sm border border-light-border dark:border-dark-border">
            <h3 className="font-bold text-light-text dark:text-dark-text mb-4">Timeline</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                  <Calendar className="text-blue-600 dark:text-blue-400" size={20} />
                </div>
                <div>
                  <p className="text-sm text-light-subtle dark:text-dark-subtle">Start Date</p>
                  <p className="font-medium text-light-text dark:text-dark-text">
                    {new Date(project.start_date).toLocaleDateString('en-GB', { 
                      day: 'numeric', month: 'long', year: 'numeric' 
                    })}
                  </p>
                </div>
              </div>
              
              {project.end_date && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
                    <CheckCircle2 className="text-green-600 dark:text-green-400" size={20} />
                  </div>
                  <div>
                    <p className="text-sm text-light-subtle dark:text-dark-subtle">Expected Completion</p>
                    <p className="font-medium text-light-text dark:text-dark-text">
                      {new Date(project.end_date).toLocaleDateString('en-GB', { 
                        day: 'numeric', month: 'long', year: 'numeric' 
                      })}
                    </p>
                  </div>
                </div>
              )}

              {project.actual_completion_date && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
                    <CheckCircle2 className="text-green-600 dark:text-green-400" size={20} />
                  </div>
                  <div>
                    <p className="text-sm text-light-subtle dark:text-dark-subtle">Actual Completion</p>
                    <p className="font-medium text-light-text dark:text-dark-text">
                      {new Date(project.actual_completion_date).toLocaleDateString('en-GB', { 
                        day: 'numeric', month: 'long', year: 'numeric' 
                      })}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Property Info */}
          <div className="bg-light-card dark:bg-dark-card p-6 rounded-xl shadow-sm border border-light-border dark:border-dark-border">
            <h3 className="font-bold text-light-text dark:text-dark-text mb-4">Property</h3>
            <div className="flex items-center gap-3">
              <Building2 className="text-light-subtle dark:text-dark-subtle" size={20} />
              <span className="text-light-text dark:text-dark-text">{project.property_name || 'N/A'}</span>
            </div>
          </div>

          {/* Contractor */}
          {project.contractor && (
            <div className="bg-light-card dark:bg-dark-card p-6 rounded-xl shadow-sm border border-light-border dark:border-dark-border">
              <h3 className="font-bold text-light-text dark:text-dark-text mb-4">Contractor</h3>
              <div className="space-y-3">
                {project.contractor.name && (
                  <div className="flex items-center gap-3">
                    <User className="text-light-subtle dark:text-dark-subtle" size={18} />
                    <span className="text-light-text dark:text-dark-text">{project.contractor.name}</span>
                  </div>
                )}
                {project.contractor.company && (
                  <div className="flex items-center gap-3">
                    <Building2 className="text-light-subtle dark:text-dark-subtle" size={18} />
                    <span className="text-light-text dark:text-dark-text">{project.contractor.company}</span>
                  </div>
                )}
                {project.contractor.phone && (
                  <div className="flex items-center gap-3">
                    <User className="text-light-subtle dark:text-dark-subtle" size={18} />
                    <span className="text-light-text dark:text-dark-text">{project.contractor.phone}</span>
                  </div>
                )}
                {project.contractor.email && (
                  <div className="flex items-center gap-3">
                    <User className="text-light-subtle dark:text-dark-subtle" size={18} />
                    <span className="text-light-text dark:text-dark-text">{project.contractor.email}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Photo Gallery */}
      {project.photos && project.photos.length > 0 && (
        <div className="bg-light-card dark:bg-dark-card p-6 rounded-xl shadow-sm border border-light-border dark:border-dark-border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-light-text dark:text-dark-text">Project Photos</h3>
            <button className="flex items-center gap-2 text-sm text-primary hover:underline">
              <Upload size={16} />
              Add Photos
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {project.photos.map((photo) => (
              <div key={photo.id} className="relative group">
                <img
                  src={photo.url}
                  alt={photo.caption || 'Project photo'}
                  className="w-full h-48 object-cover rounded-lg"
                />
                {photo.caption && (
                  <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs px-2 py-1 rounded-b-lg">
                    {photo.caption}
                  </div>
                )}
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    photo.type === 'before' ? 'bg-blue-500 text-white' :
                    photo.type === 'during' ? 'bg-yellow-500 text-white' :
                    'bg-green-500 text-white'
                  }`}>
                    {photo.type}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-light-card dark:bg-dark-card p-6 rounded-xl max-w-md mx-4">
            <h3 className="text-xl font-bold text-light-text dark:text-dark-text mb-4">Delete Project?</h3>
            <p className="text-light-subtle dark:text-dark-subtle mb-6">
              Are you sure you want to delete "{project.name}"? This action cannot be undone.
            </p>
            <div className="flex gap-4">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 py-2 border border-light-border dark:border-dark-border rounded-lg hover:bg-light-bg dark:hover:bg-dark-bg transition-colors text-light-text dark:text-dark-text"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="flex-1 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Change Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-light-card dark:bg-dark-card p-6 rounded-xl max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-light-text dark:text-dark-text">Change Status</h3>
              <button onClick={() => setShowStatusModal(false)} className="text-light-subtle hover:text-light-text dark:hover:text-dark-text">
                <X size={24} />
              </button>
            </div>
            <div className="space-y-2">
              {(['Planned', 'In Progress', 'Completed', 'On Hold', 'Cancelled'] as ProjectStatus[]).map((status) => (
                <button
                  key={status}
                  onClick={() => handleStatusChange(status)}
                  className={`w-full p-3 rounded-lg text-left transition-colors ${
                    project.status === status 
                      ? 'bg-primary/10 border-2 border-primary' 
                      : 'bg-light-bg dark:bg-dark-bg hover:bg-light-card dark:hover:bg-dark-card border-2 border-transparent'
                  }`}
                >
                  <span className={`px-2 py-1 rounded-full text-sm font-medium ${statusColors[status]}`}>
                    {status}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Progress Update Modal */}
      {showProgressModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-light-card dark:bg-dark-card p-6 rounded-xl max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-light-text dark:text-dark-text">Update Progress</h3>
              <button onClick={() => setShowProgressModal(false)} className="text-light-subtle hover:text-light-text dark:hover:text-dark-text">
                <X size={24} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-light-text dark:text-dark-text mb-2">
                  Completion Percentage: {newProgress}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={newProgress}
                  onChange={(e) => setNewProgress(parseInt(e.target.value))}
                  className="w-full h-2 bg-light-bg dark:bg-dark-bg rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-light-subtle dark:text-dark-subtle mt-1">
                  <span>0%</span>
                  <span>50%</span>
                  <span>100%</span>
                </div>
              </div>
              <div className="flex gap-4">
                <button
                  onClick={() => setShowProgressModal(false)}
                  className="flex-1 py-2 border border-light-border dark:border-dark-border rounded-lg hover:bg-light-bg dark:hover:bg-dark-bg transition-colors text-light-text dark:text-dark-text"
                >
                  Cancel
                </button>
                <button
                  onClick={handleProgressUpdate}
                  className="flex-1 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Update
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};